import Vue from "vue";

import cepFieldComponent from "@/components/child/form/cepfield.vue";
import comboComponent from "@/components/child/form/combo.vue";
import cpfCnpjComponent from "@/components/child/form/cpfCnpj.vue";
import { Component, Watch } from "@/decorators";
import CepModel from "@/models/cepModel";
import ClienteEnderecoEntregaModel from "@/models/clienteEnderecoEntregaModel";
import ClienteModel from "@/models/clienteModel";
import ClienteTelefoneModel from "@/models/clienteTelefoneModel";
import EnumExtensions from "@/models/enum/extensions/enumExtensions";
import TiposPessoa from "@/models/enum/tiposPessoa";
import Ufs from "@/models/enum/ufs";
import ValidationErrorModel from "@/models/validationErrorModel";
import ClienteService from "@/services/clienteService";
import CidadeService from "@/services/external/cidadeService";
import ValidationErrorWrapper from "@/wrappers/validationErrorWrapper";

import ShortcutComponent from "../shortcut/shortcut";
import shortcutComponent from "../shortcut/shortcut.vue";

@Component({
    components: {
        cepFieldComponent,
        comboComponent,
        cpfCnpjComponent,
        shortcutComponent,
    },
})
export default class EnderecoEntregaTerceiroComponent extends Vue {
    private shortcutComponent: ShortcutComponent = null;
    private clienteService = new ClienteService();
    private cidadeService = new CidadeService();
    private validationErrorWrapper = new ValidationErrorWrapper(this.$validator);

    model = new ClienteEnderecoEntregaModel();
    cidadesOptions: Array<Object> = [];
    ufOptions: Array<Object> = EnumExtensions.getNamesAndValuesOrderedByNames(Ufs);

    public show() {
        this.clear();
        this.shortcutComponent.title = this.$t(
            "__.Components.parent.venda.enderecoEntregaTerceiro_vue_html.novoCliente",
        ) as string;
        this.shortcutComponent.show();
    }

    public clear() {
        this.model = new ClienteEnderecoEntregaModel();
        this.cidadesOptions = [];
        this.validationErrorWrapper.clearErrors();
    }

    // @ts-ignore
    @Watch("model.estadoId")
    private async onChangeEstadoId(estadoId: Ufs) {
        if (estadoId) {
            await this.loadCidades(estadoId, this.model.cidadeId).withLoading();
        } else {
            this.cidadesOptions = [];
            this.model.cidadeId = null;
        }
    }

    private async loadCidades(estadoId: Ufs, cidadeId?: number) {
        this.cidadesOptions = await this.cidadeService.get(estadoId);

        const temCidade = this.cidadesOptions.some(item => item["value"] == cidadeId);
        this.model.cidadeId = temCidade ? cidadeId : null;
    }

    private onCepInvalid() {
        this.$showInfo(this.$t("__.ts.pesqCEP"), this.$t("__.ts.pesqNaoEncResul"));
    }

    private onCepChanged(dadosCep: CepModel) {
        this.model.bairro = dadosCep.bairro;
        this.model.endereco = dadosCep.endereco;
        this.model.complemento = dadosCep.complemento;
        this.model.cidadeId = dadosCep.cidadeId;
        this.model.estadoId = dadosCep.uf;
    }

    private async onSalvar() {
        await this.salvarCliente();
    }

    private async salvarCliente() {
        const isValid = await this.$validator.validateAll();
        if (!isValid) {
            return;
        }

        if (this.model.cidadeId) {
            this.model.cidade = this.cidadesOptions.find(item => item["value"] == this.model.cidadeId)["text"];
        }

        try {
            const response = await this.clienteService.insert(this.montarCliente()).withLoading().resolveWithResponse();

            if (!response.ok) {
                const data = (await response.json()) as ValidationErrorModel[];
                this.validationErrorWrapper.showErrors(data);
                return;
            }

            await this.$showInclusaoUpdate(null);

            this.$emit("save-ok", Number(response.headers.get("Id")));

            this.shortcutComponent.hidewithoudOutputEvent();
            this.clear();
        } catch {}
    }

    private getDocumento() {
        return (this.model.cpfCnpjEntrega ?? "").replace(/[^0-9A-Za-z]/g, "").toUpperCase();
    }

    private getTipoPessoa() {
        return this.getDocumento().length > 11 ? TiposPessoa.Juridica : TiposPessoa.Fisica;
    }

    private montarCliente() {
        const cliente = new ClienteModel();
        const documento = this.getDocumento();
        const pessoaJuridica = this.getTipoPessoa() == TiposPessoa.Juridica;

        cliente.tipoPessoa = this.getTipoPessoa();

        if (pessoaJuridica) {
            cliente.razaoSocial = this.model.nomeEntrega;
            cliente.nomeFantasia = this.model.nomeEntrega;
            cliente.cnpj = documento;
        } else {
            cliente.nome = this.model.nomeEntrega;
            cliente.cpf = documento;
        }

        cliente.nomeRotulo = (this.model.nomeEntrega ?? "").split(" ")[0];
        cliente.cep = this.model.cep;
        cliente.endereco = this.model.endereco;
        cliente.numero = this.model.numero;
        cliente.complemento = this.model.complemento;
        cliente.bairro = this.model.bairro;
        cliente.cidadeId = this.model.cidadeId;
        cliente.cidade = this.model.cidade;
        cliente.estadoId = this.model.estadoId;

        const telefone = new ClienteTelefoneModel();
        telefone.telefone = this.model.telefoneEntrega;
        telefone.padrao = true;
        cliente.telefones = [telefone];

        cliente.enderecosEntrega = [this.montarEnderecoEntrega()];

        return cliente;
    }

    private montarEnderecoEntrega() {
        const endereco = new ClienteEnderecoEntregaModel();

        endereco.cep = this.model.cep;
        endereco.endereco = this.model.endereco;
        endereco.numero = this.model.numero;
        endereco.complemento = this.model.complemento;
        endereco.bairro = this.model.bairro;
        endereco.cidadeId = this.model.cidadeId;
        endereco.cidade = this.model.cidade;
        endereco.estadoId = this.model.estadoId;
        endereco.cpfCnpjEntrega = this.model.cpfCnpjEntrega;
        endereco.tipoPessoaEntrega = this.getTipoPessoa();
        endereco.enderecoPadrao = true;
        endereco.ativo = true;

        return endereco;
    }

    private mounted() {
        this.shortcutComponent = this.$refs.shortcutComponent as ShortcutComponent;
    }
}
